import React from 'react';
import styled, { css } from 'styled-components';
import { FaSpinner } from 'react-icons/fa';

const ButtonVariants = {
  primary: css`
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primaryDark};
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.colors.secondary};
    color: white;
    
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.secondaryDark};
    }
  `,
  accent: css`
    background-color: ${({ theme }) => theme.colors.accent};
    color: white;
    
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.accentDark};
    }
  `,
  outline: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.primary};
    border: 2px solid ${({ theme }) => theme.colors.primary};
    
    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primary};
      color: white;
    }
  `,
  ghost: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.textPrimary};
    
    &:hover:not(:disabled) {
      background-color: rgba(0, 0, 0, 0.05);
    }
  `
};

const ButtonSizes = {
  small: css`
    padding: 0.5rem 1rem;
    font-size: ${({ theme }) => theme.typography.sizes.sm};
  `,
  medium: css`
    padding: 0.75rem 1.5rem;
    font-size: ${({ theme }) => theme.typography.sizes.base};
  `,
  large: css`
    padding: 1rem 2rem;
    font-size: ${({ theme }) => theme.typography.sizes.md};
  `
};

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  transition: ${({ theme }) => theme.transition.default};
  border: none;
  outline: none;
  cursor: pointer;
  position: relative;
  gap: 0.5rem;
  
  ${({ $variant }) => ButtonVariants[$variant] || ButtonVariants.primary}
  ${({ $size }) => ButtonSizes[$size] || ButtonSizes.medium}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

const SpinnerContainer = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  type = 'button',
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  ...props
}) => {
  return (
    <StyledButton
      type={type}
      $variant={variant}
      $size={size}
      disabled={disabled || loading}
      $fullWidth={fullWidth}
      onClick={onClick}
      {...props}
    >
      {loading ? (
        <>
          <SpinnerContainer>
            <FaSpinner size={16} />
          </SpinnerContainer>
          {children}
        </>
      ) : (
        children
      )}
    </StyledButton>
  );
};

export default Button; 